import React from 'react';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import {
  getBRKTransferType,
  getRetirementTypeDisplay,
  getURLTickers
} from '../ProfileScreen/util';
import config from '../../config';
import styles from './styles.css';
import { navigateToLoginFromChecklist } from './utils';
import { redirectToAccountFinder } from '../../flows/shared/utils';

const StepsSection = ({ transferAccType, fromIRAAccountType }) => {
  const brkTransferType = getBRKTransferType(fromIRAAccountType);
  const isSEPAccType = fromIRAAccountType === 'SEP';
  const mfLinkText = isSEPAccType
    ? `T. Rowe Price Mutual Funds.`
    : `Research Mutual Funds`;

  return (
    <>
      <section>
        <div className="checklist-section-title">
          Opening an account takes 5-10 minutes:
        </div>
        <dl>
          <ol>
            <li aria-describedby="basic-investment-info">
              <dt data-name="1">Provide basic investment details</dt>
              <dd id="basic-investment-info">
                This helps us tailor your investment options.
              </dd>
            </li>
            <li>
              <dt data-name="2">
                Choose Mutual Funds for your{' '}
                {isSEPAccType ? 'Traditional IRA' : transferAccType}
              </dt>
              <dd data-content="2-content">
                {isSEPAccType && <>Research more than 100 </>}
                <a
                  href="https://www.troweprice.com/personal-investing/tools/fund-research"
                  target="_blank"
                  rel="noopener noreferrer">
                  {mfLinkText}
                </a>{' '}
                {isSEPAccType || (
                  <>
                    {' '}
                    or{' '}
                    <a
                      href={`${config(
                        'returnUrlPrefix'
                      )}personal-investing/new-account/setup/account-info/brk-checklist/${brkTransferType}-ira`}
                      target="_blank"
                      rel="noopener noreferrer">
                      switch to a Brokerage {transferAccType} account
                    </a>{' '}
                    to invest in ETFs, stocks, and other securities.
                  </>
                )}
              </dd>
            </li>
            <li aria-describedby="personal-details-info">
              <dt data-name="3">Provide personal details</dt>
              <dd id="personal-details-info">
                We&apos;ll use these to secure and verify your account.
              </dd>
            </li>
            <li className="mailbox" aria-describedby="transfer-form-info">
              <dt data-name="4">Move assets into your new account</dt>
              <dd id="transfer-form-info">
                Once your account is established, mail in the Mutual Fund
                Transfer form from the confirmation page.
              </dd>
            </li>
          </ol>
        </dl>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const ChecklistSection = () => {
  return (
    <>
      <section>
        <div className="checklist-section-title">You will need:</div>
        <ul>
          <li>A minimum of $1,000</li>
          <li>Name of the institution(s) that hold your account(s)</li>
          <li>Your account number(s)</li>
          <li>Your Social Security number</li>
          <li>Permanent U.S. residence information</li>
        </ul>
      </section>
      <style jsx>{styles}</style>
    </>
  );
};

const TransferChecklistScreen = ({ appState, next }) => {
  const {
    store: { authenticated, app }
  } = appState;

  const { fromIRAAccountType } = app;

  const transferAccType = getRetirementTypeDisplay(fromIRAAccountType);

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  return (
    <div className="checklist-screen">
      <Heading text={`Transfer a ${transferAccType}`} />
      <SubHeading
        text={`Consolidate existing ${transferAccType}s into one easy-to-manage IRA. `}>
        <a
          href="https://www.troweprice.com/personal-investing/accounts/retirement/ira/rollover-401k-and-transfer-ira/index.html"
          target="_blank"
          rel="noopener noreferrer">
          Learn more about Rollover and Transfer accounts.
        </a>
      </SubHeading>

      <div className="qv-row">
        <div className="qv-col col-left">
          <StepsSection
            transferAccType={transferAccType}
            fromIRAAccountType={fromIRAAccountType}
          />
        </div>

        <div className="qv-col col-right">
          <ChecklistSection />
        </div>
      </div>

      <div className="button-container">
        {authenticated ? (
          <MFBeaconButton
            variant="primary"
            size="normal"
            id="checklistContinueExisting"
            onClick={next(multiBuyTickers)}
            label="Continue Opening an Account"
            data-name="transferChecklistContinue"
          />
        ) : (
          <div className="login-buttons">
            <MFBeaconButton
              variant="primary"
              size="normal"
              id="checklistLogin"
              onClick={() => navigateToLoginFromChecklist('/profile')}
              label="Already a Client? Log In to Continue"
              data-name="transferChecklistLogin"
            />
            <MFBeaconButton
              variant="secondary"
              size="normal"
              id="checklistContinueNew"
              onClick={next(multiBuyTickers)}
              label="New to T. Rowe Price? Start Now"
              data-name="transferChecklistNewStart"
            />
          </div>
        )}
        <MFBeaconButton
          variant="tertiary"
          size="normal"
          id="checklistBack"
          onClick={redirectToAccountFinder}
          label="Back"
        />
      </div>
      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

export default TransferChecklistScreen;
